import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE } from "@/lib/site";
import { CheckCircle2, Globe2, ShieldCheck, Users } from "lucide-react";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: `Nosotros — ${SITE.brand.name}` },
      {
        name: "description",
        content:
          "En Mostrando México conectamos viajeros con la auténtica esencia del país: tours seguros, guías certificados y experiencias inolvidables.",
      },
      { property: "og:title", content: `Nosotros — ${SITE.brand.name}` },
      { property: "og:description", content: SITE.brand.description },
      { property: "og:url", content: "/about" },
    ],
    links: [{ rel: "canonical", href: "https://www.mostrandomexico.mx/about" }],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
        <header className="mb-12 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            Sobre nosotros
          </p>
          <h1 className="mt-3 font-display text-4xl font-semibold sm:text-5xl">
            Conectamos viajeros con el alma de México
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
            En {SITE.brand.name} creemos que viajar es mucho más que conocer un destino:
            es vivir experiencias únicas, descubrir culturas fascinantes y crear recuerdos
            inolvidables. Nos especializamos en ofrecer los mejores tours y actividades para
            que disfrutes lo mejor de México de manera auténtica y emocionante.
          </p>
        </header>

        <section className="grid gap-6 md:grid-cols-3">
          <Card icon={Globe2} title="Misión">
            Ser el puente que conecta a viajeros curiosos con la auténtica esencia de México,
            diseñando tours excepcionales y seguros que revelan su riqueza cultural, histórica
            y natural.
          </Card>
          <Card icon={Users} title="Visión">
            Convertirnos en la comunidad turística de referencia en México, reconocida por
            ofrecer experiencias íntimas, responsables y memorables en cada recorrido.
          </Card>
          <Card icon={ShieldCheck} title="Valores">
            Autenticidad, seguridad, hospitalidad mexicana, sostenibilidad y trato cercano
            en cada interacción con nuestros viajeros.
          </Card>
        </section>

        <section className="mt-16 rounded-3xl border border-border bg-card p-8 shadow-card sm:p-10">
          <h2 className="font-display text-2xl font-semibold sm:text-3xl">¿Por qué elegirnos?</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            <Feature title="Precios competitivos">
              Diseñados para ofrecerte el máximo valor: logística impecable, guías certificados
              y experiencias exclusivas sin pagar de más.
            </Feature>
            <Feature title="Reserva rápida y segura">
              Sistema de reservas en línea intuitivo, disponibilidad en tiempo real y
              confirmación en segundos.
            </Feature>
            <Feature title="Visitas guiadas">
              Guías certificados, narradores apasionados y expertos locales disponibles en
              varios idiomas.
            </Feature>
            <Feature title="Cambio de fecha gratis hasta 24 h antes">
              Sujeto a disponibilidad. Con más de 24 h, reembolso del total menos 5% de cargo
              administrativo; por clima u operador, reembolso total o cambio de fecha.
            </Feature>
            <Feature title="Atención 24/7">
              Equipo disponible por WhatsApp y teléfono para resolver cualquier duda antes,
              durante y después de tu viaje.
            </Feature>
            <Feature title="Pagos seguros">
              Aceptamos {SITE.policies.payments.join(", ")}.
            </Feature>
          </div>
        </section>

        <section className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-3xl bg-primary/10 p-8 text-center sm:text-left">
          <div>
            <h3 className="font-display text-xl font-semibold">¿Listo para tu próxima aventura?</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Explora nuestros tours y experiencias en {SITE.destinations.length}+ destinos.
            </p>
          </div>
          <Link
            to="/tours"
            className="inline-flex items-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-glow transition-transform hover:scale-[1.02]"
          >
            Descubrir tours
          </Link>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function Card({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Globe2;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <Icon className="h-7 w-7 text-primary" />
      <h3 className="mt-3 font-display text-lg font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{children}</p>
    </div>
  );
}

function Feature({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
      <div>
        <h4 className="text-sm font-semibold">{title}</h4>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}
